/**
 * Minimal loader + typings for Sign in with Apple JS.
 * https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js
 *
 * The SDK is only loaded by the Apple button itself, so pages without Apple
 * sign-in never pay for the script.
 */
export const APPLE_ID_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export type AppleIdAuthInitOptions = {
  clientId: string;
  scope: string;
  redirectURI: string;
  state?: string;
  nonce?: string;
  usePopup?: boolean;
};

export type AppleIdSignInResponse = {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  /** Only present on the user's first authorization for this app. */
  user?: {
    email?: string;
    name?: { firstName?: string; lastName?: string };
  };
};

export type AppleIdSignInError = {
  error: string;
};

export type AppleIdNamespace = {
  auth: {
    init: (options: AppleIdAuthInitOptions) => void;
    signIn: () => Promise<AppleIdSignInResponse>;
  };
};

declare global {
  interface Window {
    AppleID?: AppleIdNamespace;
  }
}

let sdkPromise: Promise<AppleIdNamespace> | undefined;

export const loadAppleIdSdk = (): Promise<AppleIdNamespace> => {
  if (window.AppleID) {
    return Promise.resolve(window.AppleID);
  }
  if (!sdkPromise) {
    sdkPromise = new Promise<AppleIdNamespace>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = APPLE_ID_SDK_URL;
      script.async = true;
      script.onload = () => {
        if (window.AppleID) {
          resolve(window.AppleID);
        } else {
          reject(new Error('Sign in with Apple JS did not initialise'));
        }
      };
      script.onerror = () => {
        reject(new Error('Failed to load Sign in with Apple JS'));
      };
      document.head.appendChild(script);
    }).catch(error => {
      // Allow a later click to retry instead of caching the failure forever.
      sdkPromise = undefined;
      throw error;
    });
  }
  return sdkPromise;
};

/** Apple rejects `signIn()` with `{ error: 'popup_closed_by_user' }` on cancel. */
export const isAppleUserCancel = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as AppleIdSignInError).error === 'popup_closed_by_user';

/** Apple sends the name as parts (first-auth only); the API takes one string. */
export const appleDisplayName = (
  user: AppleIdSignInResponse['user']
): string | undefined => {
  const name = [user?.name?.firstName, user?.name?.lastName]
    .filter(part => part && part.trim())
    .join(' ')
    .trim();
  return name || undefined;
};
